/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DefenceSecurityDesks.module.scss';

type Desk = {
  n: string;
  tag: string;
  title: string;
  text: string;
  decides: string;
};

type Translate = (key: string) => string;

const getDesks = (t: Translate): Desk[] => [
  {
    n: '01',
    tag: t('Trade compliance'),
    title: t('Export control and licensing'),
    text: t(
      'Counterparty ownership resolved against Entity List, MEU and SDN designations, with aggregate holdings calculated at each level of the chain.',
    ),
    decides: t('Whether a licence is required before the item moves.'),
  },
  {
    n: '02',
    tag: t('Procurement'),
    title: t('Supplier assurance'),
    text: t(
      'Entity verification, ultimate ownership, foreign control, financial filings and adverse events across the whole supplier base and its sub-tiers.',
    ),
    decides: t('Whether a supplier can be onboarded, and on what terms.'),
  },
  {
    n: '03',
    tag: t('Corporate development'),
    title: t('Investment screening'),
    text: t(
      'Acquirer ownership traced to ultimate beneficial owners across jurisdictions, for mandatory and voluntary national security notifications.',
    ),
    decides: t('What has to be notified, and what the filing must disclose.'),
  },
  {
    n: '04',
    tag: t('Financial crime'),
    title: t('Counter-proliferation'),
    text: t(
      'Procurement networks mapped through shared officers, addresses and ownership, with incorporation and dissolution patterns across jurisdictions.',
    ),
    decides: t('Which counterparties sit inside a network rather than alone.'),
  },
  {
    n: '05',
    tag: t('Third-party risk'),
    title: t('Government contracting'),
    text: t(
      'Standing due diligence across subcontractors, resellers and distributors, with changes in control surfaced when they are filed.',
    ),
    decides: t('Whether the assurance given at award still holds.'),
  },
  {
    n: '06',
    tag: t('Analysis'),
    title: t('Research and open source'),
    text: t(
      'Corporate structures across 200+ countries as a documented research input, with every field traceable to the filing it came from.',
    ),
    decides: t('Findings that hold up when the working is checked.'),
  },
];

export const DefenceSecurityDesks = () => {
  const { t } = useInt();
  const desks = getDesks(t);
  const gridRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(gridRef, { threshold: 0.1 });

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock title={t('Six functions reading one source of record.')} />
      </div>

      <div className={cn(styles.grid, revealed && styles.gridOn)} ref={gridRef}>
        {desks.map((desk, i) => (
          <div className={styles.card} style={{ '--d': `${(i % 3) * 60}ms` } as CSSProperties} key={desk.n}>
            <div className={styles.cardTop}>
              <span className={styles.n}>{desk.n}</span>
              <span className={styles.tag}>{desk.tag}</span>
            </div>
            <h3 className={styles.title}>{desk.title}</h3>
            <p className={styles.text}>{desk.text}</p>
            <p className={styles.decides}>{desk.decides}</p>
          </div>
        ))}
      </div>

      <p className={styles.footnote}>
        {t(
          'Global Database provides corporate registry and ownership records for compliance, procurement and research. It is company information, not a capability directed at individuals.',
        )}
      </p>
    </Section>
  );
};
